'use client';

import * as React from 'react';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { updateOrganisation } from '@/services/organisation';
import type { Organisation, UpdateOrganisationDto } from '@/types';

interface EditOrganisationDialogProps {
  organisation: Organisation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function EditOrganisationDialog({
  organisation,
  open,
  onOpenChange,
  onSuccess,
}: EditOrganisationDialogProps) {
  const [name, setName] = React.useState(organisation.name);

  const queryClient = useQueryClient();

  // Update form data when organisation changes
  React.useEffect(() => {
    setName(organisation.name);
  }, [organisation]);

  // Mutation for updating an organisation
  const mutation = useMutation({
    mutationFn: (data: UpdateOrganisationDto) => updateOrganisation(organisation.id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organisations'] });
      toast.success(`Organisation "${data.name}" updated successfully!`);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: any) => {
      const errorMessage =
        typeof error === 'string'
          ? error
          : error?.message || 'Failed to update organisation';
      toast.error(errorMessage);
      console.error('Error updating organisation:', error);
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Organisation name is required');
      return;
    }

    const payload: UpdateOrganisationDto = {
      name: name.trim(),
    };

    mutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Organisation</DialogTitle>
          <DialogDescription>Update the organisation details below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Organisation Name */}
            <div className="grid gap-2">
              <Label htmlFor="name">
                Organisation Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                placeholder="e.g., Acme Corporation"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Updating...' : 'Update Organisation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

